import { useCallback, useEffect, useState } from "react";
import { selectAppLogout, selectAppView, useCarbonDispatch, useCarbonSelector } from "../store/carbonStore";

// Styles:
import "./Main.scss";
// import "./../styles/UFormFields.scss";
import "./../styles/badges.scss";
import "./../styles/buttons.scss";
import "./../styles/clusters.scss";
import "./../styles/confirmationDialog.scss";
import "./../styles/Dropdown.scss";
import "./../styles/dropdown.scss";
import "./../styles/forms.scss";
import "./../styles/multiStateToggle.scss";
import "./../styles/MultiStateToggle.scss";
import "./../styles/Responsive.scss";

import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { APP_IDS } from "userful-chronos-app-common-js/dist/models/common";
import { GcmUserPermission } from "userful-chronos-app-common-js/dist/models/gcm-user-permission";
import { ItemsControlProvider } from "userful-chronos-common-store/dist/itemsControl";
import { NavBar } from "userful-chronos-common-ui/dist";
import { ErrorOverlay } from "userful-chronos-common-ui/dist/config";
import { ToasterProvider } from "userful-chronos-common-ui/dist/elements";
import { useAuthenticationContext } from "../auth/AuthenticationContext";
import EmptyPlaceholder from "../common/components/placeholders/EmptyPlaceholder";
import { hasAtLeastOnePermission } from "../model/CarbonPermissionModels";
import { AppSliceActions } from "../store/app/appSlice";
import { IAppView } from "../store/app/appUtils";
import "./../messages/messageReceiver";
import Contents from "./contents/Contents";
import Navigation from "./navigation/Navigation";

export default function Main() {
    const dispatch = useCarbonDispatch();

    const [showNavigation, setShowNavigation] = useState(window.innerWidth <= 768 ? false : true);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [prevScreenWidth, setPrevScreenWidth] = useState(window.innerWidth);

    const appView: IAppView = useCarbonSelector(selectAppView);
    const permissions: GcmUserPermission = getGlobalStates().gcmUserPermission;
    const appLogout: boolean = useCarbonSelector(selectAppLogout);

    const { logout } = useAuthenticationContext();
    let logoutAndRefreshPage = useCallback(() => {
        logout();
        localStorage.removeItem("authData");
    }, [logout]);

    useEffect(() => {
        if (appLogout) logoutAndRefreshPage();
    }, [appLogout]);

    useEffect(() => {
        if (appView.type === "CONSTELLATIONS" && !permissions.isConstellationViewer && permissions.isUserViewer) {
            dispatch(AppSliceActions.setAppView({ type: "USER_GROUPS", id: { value: "NOT_SET" } }));
        }
    }, [permissions]);

    useEffect(() => {
        const handleResize = () => {
            setPrevScreenWidth(screenWidth); // Store previous width
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [screenWidth]);

    useEffect(() => {
        if (prevScreenWidth <= 768 && screenWidth > 768) {
            setShowNavigation(true); // Show when moving above 768px
        } else if (prevScreenWidth > 768 && screenWidth <= 768) {
            setShowNavigation(false); // Hide when moving below 768px
        }
    }, [screenWidth, prevScreenWidth]);

    return (
        <ToasterProvider>
            <ItemsControlProvider>
                <>
                    <ErrorOverlay />
                    <NavBar
                        appTitleBar={APP_IDS.GCM}
                        navigation={() => {
                            setShowNavigation(!showNavigation);
                        }}
                        helpURL="https://docs.userful.com/userful-infinity/12.10/global-cluster-manager-beta"
                        hideResetNotification
                        onLogout={() => logoutAndRefreshPage()}
                    />
                    <div className="gcm-app-wrapper">
                        {hasAtLeastOnePermission(permissions) ? (
                            <>
                                <Navigation
                                    show={showNavigation}
                                    onAction={() => {
                                        if (screenWidth <= 768 && showNavigation) {
                                            setShowNavigation(false);
                                        }
                                    }}
                                />
                                <Contents show={showNavigation} permissions={permissions} />
                            </>
                        ) : (
                            <EmptyPlaceholder
                                img="./images/notFound.png"
                                title={"User Permissions"}
                                description={
                                    "Unfortunately, it appears that you do not currently have permissions. To access the desired resources or functionalities, kindly reach out to your system administrator and request the necessary permissions. Once granted, you'll be able to navigate through the system with ease. Thank you for your understanding and cooperation."
                                }
                            />
                        )}
                    </div>
                </>
            </ItemsControlProvider>
        </ToasterProvider>
    );
}
